// The Upload screen's drop zone, as a component (owner items 17 and 20,
// 2026-08-28: "this upload section does not look like the upload section in
// the actual Upload pop-up ... this one has a 'Choose File' button instead of
// accepting a click anywhere within its borders").
//
// The behaviour that matters and that the raw `<input type="file">` the two
// import dialogs used did NOT have: the whole bordered area is the target —
// click anywhere in it to browse, drag anywhere in it to drop — and the input
// itself is hidden, so no browser-chrome "Choose File" button appears.
//
// DELIBERATELY A COPY of `UploadScreen.tsx`'s markup and styling rather than a
// refactor of it: another session owns that file this pass, and the styling is
// twenty lines. If the two ever drift, `UploadScreen.tsx` is the original and
// this follows it.
import { useRef, useState } from 'react'
import { Body1, Caption1, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import type { FluentIcon } from '@fluentui/react-icons'
import { ArrowUploadRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  dropzone: {
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: '32px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  dropzoneActive: {
    // The full `border` shorthand, not `borderColor` — griffel rejects the
    // four-sided sub-shorthand (app/README.md).
    border: `2px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  disabled: { cursor: 'default', opacity: 0.6 },
})

export function FileDropZone({
  accept,
  label,
  hint,
  disabled = false,
  icon: Icon = ArrowUploadRegular,
  onFile,
}: {
  /** The file picker's `accept` list, e.g. `.csv` or `.xlsx`. */
  accept: string
  /** The line inside the zone, e.g. "Drag a file here, or click to browse". */
  label: string
  /** The smaller line under it — format and size, when there is a limit. */
  hint?: string
  disabled?: boolean
  /** THE GLYPH INSIDE THE ZONE (owner, 2026-09-14: "the icon inside the
      drag-and-drop window inside the Import pop-up should match the icon used
      for the Import button"). Defaults to the upload arrow, which is right for
      the Upload dialog this zone was lifted from; the two Import dialogs pass
      `ArrowImportRegular`, the same glyph their own header button carries, so
      the pop-up and the control that opened it say the same thing. */
  icon?: FluentIcon
  /** One file per drop or pick; a multi-file drop takes the first. */
  onFile: (file: File) => void
}) {
  const s = useStyles()
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <div
      className={mergeClasses(s.dropzone, dragging && s.dropzoneActive, disabled && s.disabled)}
      onClick={() => !disabled && inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (disabled) return
        const f = e.dataTransfer.files?.[0]
        if (f) onFile(f)
      }}
    >
      <Icon fontSize={32} />
      <Body1>{label}</Body1>
      {hint && <Caption1>{hint}</Caption1>}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        disabled={disabled}
        onChange={(e) => {
          const f = e.target.files?.[0]
          if (f) onFile(f)
          // Clear it, so re-picking the same file after a failed preview still
          // fires `change`.
          e.target.value = ''
        }}
      />
    </div>
  )
}
